import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Loader from '../loader/Loader';
import {
  setUserSessionEmail,
  setUserData,
} from '../../app/services/AuthService';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient, { getUrl } from '../../app/api/agent';
import { Alert, Button } from '@mui/material';
import DarkLogo from '../../app/common/DarkLogo';
import * as Sentry from '@sentry/react';

export default function MagicLogin() {
  const [errorMessage, setErrorMessage] = useState(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email')?.toLowerCase();
    const redirectTo = searchParams.get('redirectTo');

    if (token && email) {
      loginWithToken(email, token, redirectTo);
    } else {
      setIsLoggingIn(false);
      setErrorMessage(
        'Invalid login link. Please check your email for the correct link or request a new one.'
      );
    }
  }, [searchParams]);

  const loginWithToken = async (email, token, redirectTo) => {
    setErrorMessage(null);
    setIsNetworkError(false);
    setIsLoggingIn(true);

    if (!navigator.onLine) {
      setIsNetworkError(true);
      setIsLoggingIn(false);
      setErrorMessage(
        'You appear to be offline. Please check your internet connection and tap Retry.'
      );
      return;
    }

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Magic login attempt',
      data: { email, online: navigator.onLine },
      level: 'info',
    });

    try {
      const response = await apiClient({
        url: getUrl() + '/magic-login',
        method: 'post',
        data: { email, token },
      });

      setUserSessionEmail(response.data.user.email, response.data.token);
      setUserData(response.data.user);
      setTimeout(() => {
        navigate('/' + (redirectTo || 'home'));
      }, 3000);
    } catch (error) {
      setIsLoggingIn(false);
      console.error('Magic login error:', error);

      const status = error.response?.status;

      if (status === 401 || status === 403) {
        // Expired or invalid magic tokens are expected — don't report to Sentry
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Magic login rejected (${status})`,
          data: { email, status },
          level: 'warning',
        });
        setErrorMessage(
          'This login link has expired or has already been used. Please go back to your email and request a new link.'
        );
      } else if (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED'
      ) {
        setIsNetworkError(true);
        Sentry.addBreadcrumb({
          category: 'network',
          message: 'Network error during magic login',
          data: { code: error.code, message: error.message },
          level: 'warning',
        });
        setErrorMessage(
          error.code === 'ECONNABORTED'
            ? 'The request timed out. This can happen on slow connections — tap Retry to try again.'
            : "We couldn't log you in because the connection was lost. Please check your internet and tap Retry."
        );
      } else {
        Sentry.captureException(error, {
          user: { email },
          tags: { login_method: 'magic-token' },
          extra: {
            status,
            response_message: error.response?.data?.message,
          },
        });
        setErrorMessage(
          'Something went wrong while logging you in. Please try clicking the link in your email again, or request a new one.'
        );
      }
    }
  };

  const handleRetry = () => {
    Sentry.addBreadcrumb({
      category: 'user_action',
      message: 'Retry magic login tapped',
      level: 'info',
    });
    const token = searchParams.get('token');
    const email = searchParams.get('email')?.toLowerCase();
    const redirectTo = searchParams.get('redirectTo');
    if (token && email) {
      loginWithToken(email, token, redirectTo);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        backgroundColor: 'background.paper',
        padding: 5,
        marginTop: 5,
      }}
    >
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <DarkLogo />
        {isLoggingIn && (
          <>
            <Typography component="h1" variant="h5">
              Logging you in
            </Typography>
            <Typography component="h2" variant="h5">
              please stand by...
            </Typography>
            <Loader />
          </>
        )}
      </Box>
      <div style={{ marginTop: '10px' }}>
        {errorMessage && (
          <Alert
            severity="error"
            action={
              isNetworkError ? (
                <Button color="inherit" size="small" onClick={handleRetry}>
                  RETRY
                </Button>
              ) : null
            }
          >
            {errorMessage}
          </Alert>
        )}
      </div>
    </Container>
  );
}
